import { type FC, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { MultiSelect, type Option } from 'react-multi-select-component';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import { useAuth, useNotifications, useQueryRequest } from '../../../modules/hooks';
import type {
  LockGroupRecord,
  LockRecord,
  RoomResponse,
} from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';
import { useLockGroups } from '../../Dashboards/LockManagement/useLockGroups';
import { useLocks } from '../../Dashboards/LockManagement/useLocks';

export type LockGroupModalMode = 'create' | 'edit' | 'view';

interface LockGroupModalProps {
  show: boolean;
  mode: LockGroupModalMode;
  data?: LockGroupRecord;
  handleClose: () => void;
  onSaved?: () => void;
}

export const LockGroupModal: FC<LockGroupModalProps> = ({
  show,
  mode,
  data,
  handleClose,
  onSaved,
}) => {
  const { setSimpleToasts } = useNotifications()!;
  const { credentialsInfo } = useAuth()!;
  const { selectedProperty } = useContext(UserPropertiesContext)!;
  const propertyId = selectedProperty?.property.uuid ?? '';
  const groupsService = useLockGroups();
  const locksService = useLocks();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [appliesToAllRooms, setAppliesToAllRooms] = useState(false);
  const [selectedLocks, setSelectedLocks] = useState<Option[]>([]);
  const [selectedRooms, setSelectedRooms] = useState<Option[]>([]);
  const [locks, setLocks] = useState<LockRecord[]>([]);
  const [saving, setSaving] = useState(false);

  const readOnly = mode === 'view';

  const { data: roomsData } = useQueryRequest<RoomResponse[]>(
    endpoints.ROOMS,
    {
      authToken: credentialsInfo?.token,
      propertyId,
    },
    { manual: !credentialsInfo?.token || !propertyId },
  );

  const lockOptions = useMemo<Option[]>(
    () => locks.map((lock) => ({ label: lock.name, value: lock.uuid })),
    [locks],
  );
  const roomOptions = useMemo<Option[]>(
    () =>
      (roomsData?.result ?? []).map((room) => ({
        label: room.name,
        value: room.uuid ?? '',
      })),
    [roomsData],
  );

  useEffect(() => {
    if (!show) return;
    setName(data?.name ?? '');
    setDescription(data?.description ?? '');
    setAppliesToAllRooms(data?.appliesToAllRooms ?? false);
    setSelectedLocks(
      (data?.locks ?? []).map((lock) => ({ label: lock.name, value: lock.uuid })),
    );
    setSelectedRooms(
      (data?.rooms ?? []).map((room) => ({
        label: room.name ?? room.uuid,
        value: room.uuid,
      })),
    );
    locksService
      .list()
      .then(setLocks)
      .catch(() =>
        setSimpleToasts({ type: 'danger', message: 'Failed to load locks', show: true }),
      );
  }, [show, data, locksService, setSimpleToasts]);

  const handleSave = async () => {
    if (!name.trim()) {
      setSimpleToasts({
        type: 'danger',
        message: 'Please enter a group name',
        show: true,
      });
      return;
    }
    setSaving(true);
    const lockUuids = selectedLocks.map((option) => option.value);
    const roomUuids = appliesToAllRooms
      ? []
      : selectedRooms.map((option) => option.value);
    try {
      if (mode === 'edit' && data) {
        await groupsService.update(data.uuid, {
          name: name.trim(),
          description: description.trim() || null,
          appliesToAllRooms,
          lockUuids,
          roomUuids,
        });
      } else {
        await groupsService.create({
          name: name.trim(),
          description: description.trim() || undefined,
          appliesToAllRooms,
          lockUuids,
          roomUuids,
        });
      }
      setSimpleToasts({ type: 'success', message: 'Lock group saved', show: true });
      onSaved?.();
      handleClose();
    } catch {
      setSimpleToasts({
        type: 'danger',
        message: 'Failed to save lock group',
        show: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === 'create'
      ? 'New lock group'
      : mode === 'edit'
        ? 'Edit lock group'
        : 'View lock group';

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Group name</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. Suite Hallway"
            value={name}
            disabled={readOnly}
            onChange={(event) => setName(event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={description}
            disabled={readOnly}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Locks</Form.Label>
          <MultiSelect
            options={lockOptions}
            value={selectedLocks}
            onChange={setSelectedLocks}
            labelledBy="Select locks"
            disabled={readOnly}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            id="lock-group-all-rooms"
            label="Applies to all rooms"
            checked={appliesToAllRooms}
            disabled={readOnly}
            onChange={(event) => setAppliesToAllRooms(event.target.checked)}
          />
        </Form.Group>

        {!appliesToAllRooms && (
          <Form.Group className="mb-3">
            <Form.Label>Rooms</Form.Label>
            <MultiSelect
              options={roomOptions}
              value={selectedRooms}
              onChange={setSelectedRooms}
              labelledBy="Select rooms"
              disabled={readOnly}
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button className="cancel-button" onClick={handleClose}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && (
          <Button className="save-button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
